/**
 * RadarChart — Gráfico radar SVG para dimensões/módulos
 */
interface RadarDataPoint {
  dimension: string;
  score: number;
}

interface RadarChartProps {
  data: RadarDataPoint[];
  maxScore?: number;
  size?: number;
  title?: string;
}

export default function RadarChart({ data, maxScore = 25, size = 280, title }: RadarChartProps) {
  if (!data || data.length < 3) {
    return (
      <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
        Mínimo de 3 dimensões necessário para o radar
      </div>
    );
  }

  const cx = size / 2;
  const cy = size / 2;
  const radius = (size / 2) - 40;
  const n = data.length;
  const angleStep = (2 * Math.PI) / n;

  const getPoint = (index: number, value: number) => {
    const angle = (index * angleStep) - Math.PI / 2;
    const r = (value / maxScore) * radius;
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  };

  const levels = [0.25, 0.5, 0.75, 1.0];
  const dataPoints = data.map((d, i) => getPoint(i, d.score));

  return (
    <div>
      {title && <h4 className="text-sm font-semibold text-[#002F59] mb-2">{title}</h4>}
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto">
        {levels.map((level, li) => {
          const points = Array.from({ length: n }, (_, i) => {
            const p = getPoint(i, maxScore * level);
            return `${p.x},${p.y}`;
          }).join(" ");
          return <polygon key={li} points={points} fill="none" stroke="#e2e8f0" strokeWidth={1} />;
        })}
        {data.map((_, i) => {
          const p = getPoint(i, maxScore);
          return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#e2e8f0" strokeWidth={1} />;
        })}
        <polygon points={dataPoints.map(p => `${p.x},${p.y}`).join(" ")} fill="rgba(0,47,89,0.15)" stroke="#002F59" strokeWidth={2} />
        {dataPoints.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={4} fill="#002F59" stroke="white" strokeWidth={2} />
        ))}
        {data.map((d, i) => {
          const labelPoint = getPoint(i, maxScore * 1.2);
          const truncated = d.dimension.length > 12 ? d.dimension.slice(0, 12) + "..." : d.dimension;
          return (
            <text key={i} x={labelPoint.x} y={labelPoint.y} textAnchor="middle" dominantBaseline="middle" className="text-[10px] fill-slate-600">
              {truncated}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
